import { useState, useEffect, useCallback } from "react";
import { authFetch, API } from "@/App";
import { TrendingDown, TrendingUp } from "lucide-react";

export default function EnergyPage() {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadEnergy = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/factory/energy`);
      const json = await res.json();
      if (json.success) setReadings(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEnergy(); }, [loadEnergy]);

  const totalMW = readings.reduce((a, e) => a + (e.reading_mw || 0), 0);
  const totalCost = readings.reduce((a, e) => a + (e.monthly_cost || 0), 0);
  const renewable = readings.filter((e) => e.cost_per_unit === 0).reduce((a, e) => a + (e.reading_mw || 0), 0);

  const consumptionByArea = [
    { area: "Kilns", percentage: 52 },
    { area: "Grinding Mills", percentage: 25 },
    { area: "Crushers", percentage: 10 },
    { area: "Packing", percentage: 5 },
    { area: "Utilities", percentage: 4 },
    { area: "Lighting & HVAC", percentage: 4 },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="energy-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Energy Management</h1>
        <p className="text-slate-400 text-sm mt-1">Power consumption and cost monitoring</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5" data-testid="energy-total-consumption">
          <p className="text-slate-400 text-xs uppercase">Total Consumption</p>
          <p className="text-white text-3xl font-bold mt-1">{totalMW.toFixed(1)} <span className="text-lg text-slate-500">MW</span></p>
        </div>
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5" data-testid="energy-total-cost">
          <p className="text-slate-400 text-xs uppercase">Monthly Cost</p>
          <p className="text-white text-3xl font-bold mt-1">{(totalCost / 100000).toFixed(1)} <span className="text-lg text-slate-500">Lakh</span></p>
        </div>
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5" data-testid="energy-renewable">
          <p className="text-slate-400 text-xs uppercase">Renewable Share</p>
          <p className="text-emerald-400 text-3xl font-bold mt-1">{totalMW > 0 ? ((renewable / totalMW) * 100).toFixed(0) : 0}%</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4">Power Sources</h3>
          <div className="space-y-4">
            {readings.map((e) => (
              <div key={e.energy_id || e.source} className="flex items-center justify-between" data-testid={`energy-source-${e.energy_id}`}>
                <div>
                  <p className="text-white text-sm font-medium">{e.source}</p>
                  <p className="text-slate-500 text-xs">{e.cost_per_unit > 0 ? `Rs ${e.cost_per_unit}/kWh` : "Free"}</p>
                </div>
                <div className="text-right flex items-center gap-3">
                  <span className="text-white font-medium">{e.reading_mw} MW</span>
                  {e.trend !== undefined && (
                    <span className={`text-xs flex items-center gap-0.5 ${e.trend > 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {e.trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {Math.abs(e.trend)}%
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4">Consumption by Area</h3>
          <div className="space-y-3">
            {consumptionByArea.map((a, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-300">{a.area}</span>
                  <span className="text-white font-medium">{(totalMW * a.percentage / 100).toFixed(1)} MW ({a.percentage}%)</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${a.percentage}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
